import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { PaginationType } from '../../types/utils/pagination.type';
import { RouteParamsType } from '../../types/utils/router-params.type';
import {
  MegaswitchInstanceType,
  MegaswitchInstanceListItemType,
  CreateMegaswitchInstanceDto,
  MegaswitchInstanceUserListItemType,
  CreateMegaswitchInstanceUserDto,
  CreateMegaswitchInstanceUserResponse,
} from '../../types/megaswitch/megaswitch-instance.type';
import { setModalContent, setShowModal } from '../utils/utils.slice';
import { MegaswitchDomainType } from '../../types/megaswitch/meagswitch-domain.types';
import { MegaswitchCarrierType } from '../../types/megaswitch/megaswitch-carrier.types';
import {
  MegaswitchDidType,
  CreateMegaswitchDidDto,
  UpdateMegaswitchDidDto,
} from '../../types/megaswitch/megaswitch-did.types';
import {
  MegaswitchUserType,
  CreateMegaswitchUserDto,
  UpdateMegaswitchUserDto,
} from '../../types/megaswitch/megaswitch-user.types';
import {
  MegaswitchOutboundRouteType,
  CreateMegaswitchOutboundRouteDto,
  UpdateMegaswitchOutboundRouteDto,
} from '../../types/megaswitch/megaswitch-outbound-route.types';
import {
  MegaswitchCoreType,
  CreateMegaswitchCoreDto,
  UpdateMegaswitchCoreDto,
} from '../../types/megaswitch/megaswitch-core.types';
import { rejectWithApiError } from '../../utils/api';
import { RootReducerState } from '../../redux/Reducers';

export const getMegaswitchInstancesList = createAsyncThunk<
  PaginationType<MegaswitchInstanceListItemType>,
  RouteParamsType & { controller?: AbortController },
  { state: RootReducerState }
>('megaswitch/getMegaswitchInstancesList', async (data, thunkAPI) => {
  const { controller, ...params } = data;
  try {
    const queryString = Object.keys(params)
      .map(key => key + '=' + params[key])
      .join('&');

    const response = await axios.get<PaginationType<MegaswitchInstanceListItemType>>(
      `${process.env.REACT_APP_API_URL}/megaswitch/instance${queryString ? `?${queryString}` : ''}`,
      controller ? { signal: controller.signal } : undefined
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching megaswitch instances!' });
  }
});

export const getMegaswitchInstanceDetails = createAsyncThunk<
  MegaswitchInstanceType,
  { id: number },
  { state: RootReducerState }
>('megaswitch/getMegaswitchInstanceDetails', async ({ id }, thunkAPI) => {
  try {
    const response = await axios.get<MegaswitchInstanceType>(
      `${process.env.REACT_APP_API_URL}/megaswitch/instance/${id}`
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching megaswitch instance!' });
  }
});

export const postMegaswitchInstanceDetails = createAsyncThunk<
  MegaswitchInstanceType,
  { data: CreateMegaswitchInstanceDto },
  { state: RootReducerState }
>('megaswitch/postMegaswitchInstanceDetails', async ({ data }, thunkAPI) => {
  try {
    const response = await axios.post<MegaswitchInstanceType>(
      `${process.env.REACT_APP_API_URL}/megaswitch/instance`,
      data
    );
    thunkAPI.dispatch(getMegaswitchInstancesList(thunkAPI.getState().megaswitchReducer.megaswitchInstancesRouteParams));
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during creating megaswitch instance!' });
  }
});

export const patchMegaswitchInstanceDetails = createAsyncThunk<
  MegaswitchInstanceType,
  { data: CreateMegaswitchInstanceDto; id: number },
  { state: RootReducerState }
>('megaswitch/patchMegaswitchInstanceDetails', async ({ data, id }, thunkAPI) => {
  try {
    const response = await axios.patch<MegaswitchInstanceType>(
      `${process.env.REACT_APP_API_URL}/megaswitch/instance/${id}`,
      data
    );
    thunkAPI.dispatch(getMegaswitchInstanceDetails({ id }));
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during updating megaswitch instance!' });
  }
});

export const deleteMegaswitchInstanceDetails = createAsyncThunk<
  void,
  { id: number; fetchList: boolean },
  { state: RootReducerState }
>('megaswitch/deleteMegaswitchInstanceDetails', async ({ id, fetchList }, thunkAPI) => {
  try {
    await axios.delete(`${process.env.REACT_APP_API_URL}/megaswitch/instance/${id}`);
    if (fetchList) {
      thunkAPI.dispatch(
        getMegaswitchInstancesList(thunkAPI.getState().megaswitchReducer.megaswitchInstancesRouteParams)
      );
    }
    thunkAPI.dispatch(setShowModal(false));
    thunkAPI.dispatch(setModalContent(null));
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during deleting megaswitch instance!' });
  }
});

export const getMegaswitchInstanceUsersList = createAsyncThunk<
  PaginationType<MegaswitchInstanceUserListItemType>,
  RouteParamsType & { controller?: AbortController; instanceId: number },
  { state: RootReducerState }
>('megaswitch/getMegaswitchInstanceUsersList', async (data, thunkAPI) => {
  const { controller, instanceId, ...params } = data;
  try {
    const queryString = Object.keys(params)
      .map(key => key + '=' + params[key])
      .join('&');

    const instanceParam = `instance_id=${instanceId}`;
    const fullQuery = queryString ? `${queryString}&${instanceParam}` : instanceParam;

    const response = await axios.get<PaginationType<MegaswitchInstanceUserListItemType>>(
      `${process.env.REACT_APP_API_URL}/megaswitch/instance-user?${fullQuery}`,
      controller ? { signal: controller.signal } : undefined
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching megaswitch instance users!' });
  }
});

export const postMegaswitchInstanceUserDetails = createAsyncThunk<
  { response: CreateMegaswitchInstanceUserResponse; formData: CreateMegaswitchInstanceUserDto },
  { data: CreateMegaswitchInstanceUserDto },
  { state: RootReducerState }
>('megaswitch/postMegaswitchInstanceUserDetails', async ({ data }, thunkAPI) => {
  try {
    const response = await axios.post<CreateMegaswitchInstanceUserResponse>(
      `${process.env.REACT_APP_API_URL}/megaswitch/instance-user`,
      data
    );
    thunkAPI.dispatch(setShowModal(false));
    thunkAPI.dispatch(setModalContent(null));
    const routeParams = { ...thunkAPI.getState().megaswitchReducer.megaswitchInstanceUsersRouteParams };
    thunkAPI.dispatch(getMegaswitchInstanceUsersList({ ...routeParams, instanceId: data.instance.id }));
    return { response: response.data, formData: data };
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during creating megaswitch instance user!' });
  }
});

export const deleteMegaswitchInstanceUserDetails = createAsyncThunk<
  void,
  { id: number; instanceId: number; fetchList: boolean },
  { state: RootReducerState }
>('megaswitch/deleteMegaswitchInstanceUserDetails', async ({ id, instanceId, fetchList }, thunkAPI) => {
  try {
    await axios.delete(`${process.env.REACT_APP_API_URL}/megaswitch/instance-user/${id}`);
    if (fetchList) {
      const routeParams = { ...thunkAPI.getState().megaswitchReducer.megaswitchInstanceUsersRouteParams };
      thunkAPI.dispatch(getMegaswitchInstanceUsersList({ ...routeParams, instanceId }));
    }
    thunkAPI.dispatch(setShowModal(false));
    thunkAPI.dispatch(setModalContent(null));
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during deleting megaswitch instance user!' });
  }
});

// DOMAINS API ACTIONS
export const getMegaswitchDomainsList = createAsyncThunk<
  PaginationType<MegaswitchDomainType>,
  RouteParamsType & { esi_id: number | string; controller?: AbortController },
  { state: RootReducerState }
>('megaswitch/getMegaswitchDomainsList', async (data, thunkAPI) => {
  try {
    const { controller, ...params } = data;

    const queryString = Object.keys(params)
      .map(key => key + '=' + params[key])
      .join('&');

    const response = await axios.get<PaginationType<MegaswitchDomainType>>(
      `${process.env.REACT_APP_API_URL}/megaswitch/domain?${queryString}`,
      controller ? { signal: controller.signal } : undefined
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching megaswitch domains!' });
  }
});

export interface CreateMegaswitchDomainDto {
  externalSystemInstance: {
    id: number;
  };
  domain: string;
  active: boolean;
}

export const postMegaswitchDomainDetails = createAsyncThunk<
  MegaswitchDomainType,
  { data: CreateMegaswitchDomainDto; esi_id: number },
  { state: RootReducerState }
>('megaswitch/postMegaswitchDomainDetails', async ({ data, esi_id }, thunkAPI) => {
  try {
    const response = await axios.post<MegaswitchDomainType>(`${process.env.REACT_APP_API_URL}/megaswitch/domain`, data);
    thunkAPI.dispatch(setShowModal(false));
    thunkAPI.dispatch(setModalContent(null));
    thunkAPI.dispatch(
      getMegaswitchDomainsList({ esi_id, ...thunkAPI.getState().megaswitchReducer.megaswitchDomainsRouteParams })
    );

    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during creating megaswitch domain!' });
  }
});

export const updateMegaswitchDomainDetails = createAsyncThunk<
  MegaswitchDomainType,
  { data: { msid: number; active: boolean; externalSystemInstance: { id: number } }; esi_id: number },
  { state: RootReducerState }
>('megaswitch/updateMegaswitchDomainDetails', async ({ data, esi_id }, thunkAPI) => {
  try {
    const response = await axios.patch<MegaswitchDomainType>(
      `${process.env.REACT_APP_API_URL}/megaswitch/domain?esi_id=${esi_id}`,
      data
    );
    thunkAPI.dispatch(setShowModal(false));
    thunkAPI.dispatch(setModalContent(null));
    thunkAPI.dispatch(
      getMegaswitchDomainsList({ esi_id, ...thunkAPI.getState().megaswitchReducer.megaswitchDomainsRouteParams })
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during updating megaswitch domain!' });
  }
});

export const deleteMegaswitchDomainDetails = createAsyncThunk<
  void,
  { data: { msid: number }; esi_id: number },
  { state: RootReducerState }
>('megaswitch/deleteMegaswitchDomainDetails', async ({ data, esi_id }, thunkAPI) => {
  try {
    await axios.delete(`${process.env.REACT_APP_API_URL}/megaswitch/domain?esi_id=${esi_id}`, { data });
    thunkAPI.dispatch(setShowModal(false));
    thunkAPI.dispatch(setModalContent(null));
    thunkAPI.dispatch(
      getMegaswitchDomainsList({ esi_id, ...thunkAPI.getState().megaswitchReducer.megaswitchDomainsRouteParams })
    );
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during deleting megaswitch domain!' });
  }
});

// CARRIERS API ACTIONS
export const getMegaswitchCarriersList = createAsyncThunk<
  PaginationType<MegaswitchCarrierType>,
  RouteParamsType & { esi_id: number | string; controller?: AbortController },
  { state: RootReducerState }
>('megaswitch/getMegaswitchCarriersList', async (data, thunkAPI) => {
  try {
    const { controller, ...params } = data;

    const queryString = Object.keys(params)
      .map(key => key + '=' + params[key])
      .join('&');

    const response = await axios.get<PaginationType<MegaswitchCarrierType>>(
      `${process.env.REACT_APP_API_URL}/megaswitch/carrier?${queryString}`,
      controller ? { signal: controller.signal } : undefined
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching megaswitch carriers!' });
  }
});

export interface CreateMegaswitchCarrierDto {
  externalSystemInstance: {
    id: number;
  };
  description: string;
  inDidPrepend?: string;
  active: boolean;
}

export const postMegaswitchCarrierDetails = createAsyncThunk<
  MegaswitchCarrierType,
  { data: CreateMegaswitchCarrierDto; esi_id: number },
  { state: RootReducerState }
>('megaswitch/postMegaswitchCarrierDetails', async ({ data, esi_id }, thunkAPI) => {
  try {
    const response = await axios.post<MegaswitchCarrierType>(
      `${process.env.REACT_APP_API_URL}/megaswitch/carrier`,
      data
    );
    thunkAPI.dispatch(setShowModal(false));
    thunkAPI.dispatch(setModalContent(null));
    thunkAPI.dispatch(
      getMegaswitchCarriersList({ esi_id, ...thunkAPI.getState().megaswitchReducer.megaswitchCarriersRouteParams })
    );

    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during creating megaswitch carrier!' });
  }
});

export const updateMegaswitchCarrierDetails = createAsyncThunk<
  MegaswitchCarrierType,
  {
    data: {
      msid: number;
      description?: string;
      inDidPrepend?: string;
      active?: boolean;
      externalSystemInstance: { id: number };
    };
    esi_id: number | string;
  },
  { state: RootReducerState }
>('megaswitch/updateMegaswitchCarrierDetails', async ({ data, esi_id }, thunkAPI) => {
  try {
    const response = await axios.patch<MegaswitchCarrierType>(
      `${process.env.REACT_APP_API_URL}/megaswitch/carrier?esi_id=${esi_id}`,
      data
    );
    thunkAPI.dispatch(setShowModal(false));
    thunkAPI.dispatch(setModalContent(null));
    thunkAPI.dispatch(
      getMegaswitchCarriersList({ esi_id, ...thunkAPI.getState().megaswitchReducer.megaswitchCarriersRouteParams })
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during updating megaswitch carrier!' });
  }
});

export const deleteMegaswitchCarrierDetails = createAsyncThunk<
  void,
  { data: { msid: number }; esi_id: number },
  { state: RootReducerState }
>('megaswitch/deleteMegaswitchCarrierDetails', async ({ data, esi_id }, thunkAPI) => {
  try {
    await axios.delete(`${process.env.REACT_APP_API_URL}/megaswitch/carrier?esi_id=${esi_id}`, { data });
    thunkAPI.dispatch(setShowModal(false));
    thunkAPI.dispatch(setModalContent(null));
    thunkAPI.dispatch(
      getMegaswitchCarriersList({ esi_id, ...thunkAPI.getState().megaswitchReducer.megaswitchCarriersRouteParams })
    );
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during deleting megaswitch carrier!' });
  }
});

// DIDs API ACTIONS
export const getMegaswitchDidsList = createAsyncThunk<
  PaginationType<MegaswitchDidType>,
  RouteParamsType & { esi_id: number | string; controller?: AbortController },
  { state: RootReducerState }
>('megaswitch/getMegaswitchDidsList', async (data, thunkAPI) => {
  try {
    const { controller, ...params } = data;

    const queryString = Object.keys(params)
      .map(key => key + '=' + params[key])
      .join('&');

    const response = await axios.get<PaginationType<MegaswitchDidType>>(
      `${process.env.REACT_APP_API_URL}/megaswitch/did?${queryString}`,
      controller ? { signal: controller.signal } : undefined
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching megaswitch DIDs!' });
  }
});

export const postMegaswitchDidDetails = createAsyncThunk<
  MegaswitchDidType,
  { data: CreateMegaswitchDidDto; esi_id: number },
  { state: RootReducerState }
>('megaswitch/postMegaswitchDidDetails', async ({ data, esi_id }, thunkAPI) => {
  try {
    const response = await axios.post<MegaswitchDidType>(`${process.env.REACT_APP_API_URL}/megaswitch/did`, data);
    thunkAPI.dispatch(setShowModal(false));
    thunkAPI.dispatch(setModalContent(null));
    thunkAPI.dispatch(
      getMegaswitchDidsList({ esi_id, ...thunkAPI.getState().megaswitchReducer.megaswitchDidsRouteParams })
    );

    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during creating megaswitch DID!' });
  }
});

export const updateMegaswitchDidDetails = createAsyncThunk<
  MegaswitchDidType,
  { data: UpdateMegaswitchDidDto; esi_id: number },
  { state: RootReducerState }
>('megaswitch/updateMegaswitchDidDetails', async ({ data, esi_id }, thunkAPI) => {
  try {
    const response = await axios.patch<MegaswitchDidType>(
      `${process.env.REACT_APP_API_URL}/megaswitch/did?esi_id=${esi_id}`,
      data
    );
    thunkAPI.dispatch(setShowModal(false));
    thunkAPI.dispatch(setModalContent(null));
    thunkAPI.dispatch(
      getMegaswitchDidsList({ esi_id, ...thunkAPI.getState().megaswitchReducer.megaswitchDidsRouteParams })
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during updating megaswitch DID!' });
  }
});

export const deleteMegaswitchDidDetails = createAsyncThunk<
  void,
  { data: { msid: number }; esi_id: number },
  { state: RootReducerState }
>('megaswitch/deleteMegaswitchDidDetails', async ({ data, esi_id }, thunkAPI) => {
  try {
    await axios.delete(`${process.env.REACT_APP_API_URL}/megaswitch/did?esi_id=${esi_id}`, { data });
    thunkAPI.dispatch(setShowModal(false));
    thunkAPI.dispatch(setModalContent(null));
    thunkAPI.dispatch(
      getMegaswitchDidsList({ esi_id, ...thunkAPI.getState().megaswitchReducer.megaswitchDidsRouteParams })
    );
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during deleting megaswitch DID!' });
  }
});

// USERS API ACTIONS
export const getMegaswitchUsersList = createAsyncThunk<
  PaginationType<MegaswitchUserType>,
  RouteParamsType & { esi_id: number | string; controller?: AbortController },
  { state: RootReducerState }
>('megaswitch/getMegaswitchUsersList', async (data, thunkAPI) => {
  try {
    const { controller, ...params } = data;

    const queryString = Object.keys(params)
      .map(key => key + '=' + params[key])
      .join('&');

    const response = await axios.get<PaginationType<MegaswitchUserType>>(
      `${process.env.REACT_APP_API_URL}/megaswitch/user?${queryString}`,
      controller ? { signal: controller.signal } : undefined
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching megaswitch users!' });
  }
});

export const postMegaswitchUserDetails = createAsyncThunk<
  MegaswitchUserType,
  { data: CreateMegaswitchUserDto; esi_id: number },
  { state: RootReducerState }
>('megaswitch/postMegaswitchUserDetails', async ({ data, esi_id }, thunkAPI) => {
  try {
    const response = await axios.post<MegaswitchUserType>(
      `${process.env.REACT_APP_API_URL}/megaswitch/user?esi_id=${esi_id}`,
      data
    );
    thunkAPI.dispatch(setShowModal(false));
    thunkAPI.dispatch(setModalContent(null));
    thunkAPI.dispatch(
      getMegaswitchUsersList({ esi_id, ...thunkAPI.getState().megaswitchReducer.megaswitchUsersRouteParams })
    );

    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during creating megaswitch user!' });
  }
});

export const updateMegaswitchUserDetails = createAsyncThunk<
  MegaswitchUserType,
  { data: UpdateMegaswitchUserDto; esi_id: number },
  { state: RootReducerState }
>('megaswitch/updateMegaswitchUserDetails', async ({ data, esi_id }, thunkAPI) => {
  try {
    const response = await axios.patch<MegaswitchUserType>(
      `${process.env.REACT_APP_API_URL}/megaswitch/user?esi_id=${esi_id}`,
      data
    );
    thunkAPI.dispatch(setShowModal(false));
    thunkAPI.dispatch(setModalContent(null));
    thunkAPI.dispatch(
      getMegaswitchUsersList({ esi_id, ...thunkAPI.getState().megaswitchReducer.megaswitchUsersRouteParams })
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during updating megaswitch user!' });
  }
});

export const deleteMegaswitchUserDetails = createAsyncThunk<
  void,
  { data: { msid: number }; esi_id: number },
  { state: RootReducerState }
>('megaswitch/deleteMegaswitchUserDetails', async ({ data, esi_id }, thunkAPI) => {
  try {
    await axios.delete(`${process.env.REACT_APP_API_URL}/megaswitch/user?esi_id=${esi_id}`, { data });
    thunkAPI.dispatch(setShowModal(false));
    thunkAPI.dispatch(setModalContent(null));
    thunkAPI.dispatch(
      getMegaswitchUsersList({ esi_id, ...thunkAPI.getState().megaswitchReducer.megaswitchUsersRouteParams })
    );
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during deleting megaswitch user!' });
  }
});

// OUTBOUND ROUTES API ACTIONS
export const getMegaswitchOutboundRoutesList = createAsyncThunk<
  PaginationType<MegaswitchOutboundRouteType>,
  RouteParamsType & { esi_id: number | string; controller?: AbortController },
  { state: RootReducerState }
>('megaswitch/getMegaswitchOutboundRoutesList', async (data, thunkAPI) => {
  try {
    const { controller, ...params } = data;

    const queryString = Object.keys(params)
      .map(key => key + '=' + params[key])
      .join('&');

    const response = await axios.get<PaginationType<MegaswitchOutboundRouteType>>(
      `${process.env.REACT_APP_API_URL}/megaswitch/outbound-route?${queryString}`,
      controller ? { signal: controller.signal } : undefined
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching megaswitch outbound routes!' });
  }
});

export const postMegaswitchOutboundRouteDetails = createAsyncThunk<
  MegaswitchOutboundRouteType,
  { data: CreateMegaswitchOutboundRouteDto; esi_id: number },
  { state: RootReducerState }
>('megaswitch/postMegaswitchOutboundRouteDetails', async ({ data, esi_id }, thunkAPI) => {
  try {
    const response = await axios.post<MegaswitchOutboundRouteType>(
      `${process.env.REACT_APP_API_URL}/megaswitch/outbound-route`,
      data
    );
    thunkAPI.dispatch(setShowModal(false));
    thunkAPI.dispatch(setModalContent(null));
    thunkAPI.dispatch(
      getMegaswitchOutboundRoutesList({
        esi_id,
        ...thunkAPI.getState().megaswitchReducer.megaswitchOutboundRoutesRouteParams,
      })
    );

    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during creating megaswitch outbound route!' });
  }
});

export const updateMegaswitchOutboundRouteDetails = createAsyncThunk<
  MegaswitchOutboundRouteType,
  { data: UpdateMegaswitchOutboundRouteDto; esi_id: number },
  { state: RootReducerState }
>('megaswitch/updateMegaswitchOutboundRouteDetails', async ({ data, esi_id }, thunkAPI) => {
  try {
    const response = await axios.patch<MegaswitchOutboundRouteType>(
      `${process.env.REACT_APP_API_URL}/megaswitch/outbound-route?esi_id=${esi_id}`,
      data
    );
    thunkAPI.dispatch(setShowModal(false));
    thunkAPI.dispatch(setModalContent(null));
    thunkAPI.dispatch(
      getMegaswitchOutboundRoutesList({
        esi_id,
        ...thunkAPI.getState().megaswitchReducer.megaswitchOutboundRoutesRouteParams,
      })
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during updating megaswitch outbound route!' });
  }
});

export const deleteMegaswitchOutboundRouteDetails = createAsyncThunk<
  void,
  { data: { msid: number }; esi_id: number },
  { state: RootReducerState }
>('megaswitch/deleteMegaswitchOutboundRouteDetails', async ({ data, esi_id }, thunkAPI) => {
  try {
    await axios.delete(`${process.env.REACT_APP_API_URL}/megaswitch/outbound-route?esi_id=${esi_id}`, { data });
    thunkAPI.dispatch(setShowModal(false));
    thunkAPI.dispatch(setModalContent(null));
    thunkAPI.dispatch(
      getMegaswitchOutboundRoutesList({
        esi_id,
        ...thunkAPI.getState().megaswitchReducer.megaswitchOutboundRoutesRouteParams,
      })
    );
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during deleting megaswitch outbound route!' });
  }
});

// CORES API ACTIONS
export const getMegaswitchCoresList = createAsyncThunk<
  PaginationType<MegaswitchCoreType>,
  RouteParamsType & { esi_id: number | string; controller?: AbortController },
  { state: RootReducerState }
>('megaswitch/getMegaswitchCoresList', async (data, thunkAPI) => {
  try {
    const { controller, ...params } = data;

    const queryString = Object.keys(params)
      .map(key => key + '=' + params[key])
      .join('&');

    const response = await axios.get<PaginationType<MegaswitchCoreType>>(
      `${process.env.REACT_APP_API_URL}/megaswitch/core?${queryString}`,
      controller ? { signal: controller.signal } : undefined
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching megaswitch cores!' });
  }
});

export const postMegaswitchCoreDetails = createAsyncThunk<
  MegaswitchCoreType,
  { data: CreateMegaswitchCoreDto; esi_id: number },
  { state: RootReducerState }
>('megaswitch/postMegaswitchCoreDetails', async ({ data, esi_id }, thunkAPI) => {
  try {
    const response = await axios.post<MegaswitchCoreType>(`${process.env.REACT_APP_API_URL}/megaswitch/core`, data);
    thunkAPI.dispatch(setShowModal(false));
    thunkAPI.dispatch(setModalContent(null));
    thunkAPI.dispatch(
      getMegaswitchCoresList({ esi_id, ...thunkAPI.getState().megaswitchReducer.megaswitchCoresRouteParams })
    );

    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during creating megaswitch core!' });
  }
});

export const updateMegaswitchCoreDetails = createAsyncThunk<
  MegaswitchCoreType,
  { data: UpdateMegaswitchCoreDto; esi_id: number },
  { state: RootReducerState }
>('megaswitch/updateMegaswitchCoreDetails', async ({ data, esi_id }, thunkAPI) => {
  try {
    const response = await axios.patch<MegaswitchCoreType>(
      `${process.env.REACT_APP_API_URL}/megaswitch/core?esi_id=${esi_id}`,
      data
    );
    thunkAPI.dispatch(setShowModal(false));
    thunkAPI.dispatch(setModalContent(null));
    thunkAPI.dispatch(
      getMegaswitchCoresList({ esi_id, ...thunkAPI.getState().megaswitchReducer.megaswitchCoresRouteParams })
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during updating megaswitch core!' });
  }
});

export const deleteMegaswitchCoreDetails = createAsyncThunk<
  void,
  { data: { msid: number }; esi_id: number },
  { state: RootReducerState }
>('megaswitch/deleteMegaswitchCoreDetails', async ({ data, esi_id }, thunkAPI) => {
  try {
    await axios.delete(`${process.env.REACT_APP_API_URL}/megaswitch/core?esi_id=${esi_id}`, { data });
    thunkAPI.dispatch(setShowModal(false));
    thunkAPI.dispatch(setModalContent(null));
    thunkAPI.dispatch(
      getMegaswitchCoresList({ esi_id, ...thunkAPI.getState().megaswitchReducer.megaswitchCoresRouteParams })
    );
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during deleting megaswitch core!' });
  }
});
